import { useState } from "react";
import { Link } from "react-router-dom";
import { Eye, EyeOff, Wind } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";

export const AuthShell = ({ title, subtitle, children, footer }) => (
  <div className="min-h-[calc(100vh-4rem)] grid grid-cols-1 lg:grid-cols-12">
    <aside className="hidden lg:flex lg:col-span-5 bg-indigo-600 text-white p-12 flex-col justify-between relative overflow-hidden grain">
      <Wind className="absolute -right-16 -bottom-16 w-96 h-96 text-white/10" />
      <div className="relative">
        <p className="text-xs font-bold uppercase tracking-[0.2em] text-indigo-200">Tutorials</p>
        <h2 className="font-display text-3xl xl:text-4xl font-extrabold tracking-tight mt-4 leading-tight">A calm place to practise what you learn in therapy.</h2>
      </div>
      <p className="relative text-indigo-100 text-sm max-w-sm">Your progress and bookmarks are private. Nobody else — including the admin — can see your activity.</p>
    </aside>
    <div className="lg:col-span-7 flex items-center justify-center px-4 sm:px-6 py-12">
      <div className="w-full max-w-md space-y-8 rise">
        <div className="space-y-2">
          <h1 className="font-display text-3xl sm:text-4xl font-extrabold tracking-tight text-slate-900">{title}</h1>
          {subtitle && <p className="text-slate-600">{subtitle}</p>}
        </div>
        {children}
        {footer && <div className="text-sm text-slate-600">{footer}</div>}
      </div>
    </div>
  </div>
);

export const Field = ({ id, label, type = "text", value, onChange, placeholder, autoComplete, testId }) => (
  <div className="space-y-2">
    <Label htmlFor={id} className="text-slate-700">{label}</Label>
    <Input id={id} type={type} value={value} onChange={(e) => onChange(e.target.value)} placeholder={placeholder} autoComplete={autoComplete} required className="h-12 rounded-xl bg-white" data-testid={testId} />
  </div>
);

export const PasswordField = ({ id, label, value, onChange, autoComplete, testId }) => {
  const [show, setShow] = useState(false);
  return (
    <div className="space-y-2">
      <Label htmlFor={id} className="text-slate-700">{label}</Label>
      <div className="relative">
        <Input id={id} type={show ? "text" : "password"} value={value} onChange={(e) => onChange(e.target.value)} autoComplete={autoComplete} required minLength={8} className="h-12 rounded-xl bg-white pr-12" data-testid={testId} />
        <button type="button" onClick={() => setShow((s) => !s)} className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-700" aria-label={show ? "Hide password" : "Show password"} data-testid={`${testId}-toggle`}>
          {show ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
        </button>
      </div>
    </div>
  );
};

export const FormError = ({ message }) => message ? <p className="text-sm text-rose-600 bg-rose-50 border border-rose-100 rounded-xl px-4 py-3" role="alert" data-testid="form-error">{message}</p> : null;

export const SubmitButton = ({ loading, children, testId }) => (
  <Button type="submit" disabled={loading} className="w-full h-12 rounded-xl bg-indigo-600 hover:bg-indigo-700 text-white text-base" data-testid={testId}>
    {loading ? "Please wait…" : children}
  </Button>
);

export const AuthLink = ({ to, children, testId }) => <Link to={to} className="font-semibold text-indigo-600 hover:text-indigo-800" data-testid={testId}>{children}</Link>;
